import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import LoadingScreen from "../components/LoadingScreen";
import { toast } from "react-hot-toast";
import { PictureBook } from "../components/library/PictureBook";
import LibraryContentTabs from "../components/library/LibraryContentTabs";
import logger from "../utils/logger";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
const STORYBOOKS_PER_ROW = 5;
const DEFAULT_ROWS = 2; // Default visible rows of user's storybooks
const RECOMMENDED_LIMIT = 10;

// ── Convert an audiobook record into the shape PictureBook expects ──
const audiobookToStory = (a) => {
  const status = a.status;
  const statusLower = String(status || "").toLowerCase();
  const isReady = statusLower === "ready" || statusLower === "completed";
  return {
    id: a.id,
    name: a.title || "Untitled Storybook",
    title: a.title || "Untitled Storybook",
    thumbnail_url: a.cover_url || a.cover_image_url || a.thumbnail_url || null,
    cover_image_url: a.cover_image_url || a.cover_url || a.thumbnail_url || null,
    // Trust backend status — ready books must not look "generating".
    file_url: isReady ? "ready" : null,
    status,
    status_percentage: isReady ? 100 : a.progress || a.percentage || 0,
    creation_method: a.creation_method,
  };
};

// ── A single "shelf row" with a wooden plank beneath it ──
const ShelfRow = ({ books, offsetIndex = 0, onBookClick }) => (
  <div>
    <div
      className="grid gap-3 sm:gap-4 px-3 sm:px-4 pb-1"
      style={{
        gridTemplateColumns: `repeat(${STORYBOOKS_PER_ROW}, minmax(0, 1fr))`,
        alignItems: "end",
        justifyItems: "center",
      }}
    >
      {books.map((book, i) => (
        <PictureBook
          key={book.id || `${offsetIndex}-${i}`}
          story={book}
          index={offsetIndex + i}
          onClick={() => onBookClick?.(book)}
        />
      ))}
      {Array.from({ length: STORYBOOKS_PER_ROW - books.length }).map((_, i) => (
        <div key={`pad-${i}`} aria-hidden="true" />
      ))}
    </div>
    {/* Shelf plank */}
    <div
      aria-hidden="true"
      style={{
        position: "relative",
        height: 26,
        marginTop: -2,
        borderRadius: 10,
        background:
          "linear-gradient(180deg, #C99564 0%, #B07F4A 60%, #8E5F30 100%)",
        boxShadow:
          "0px 6px 0px rgba(11,26,51,0.10), 0px 14px 24px rgba(11,26,51,0.14)",
      }}
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 600 26"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0 }}
      >
        <path d="M0 8 Q120 12 240 8 T480 9 T600 8" stroke="#8E5F30" strokeWidth="0.6" fill="none" opacity="0.4" />
        <path d="M0 18 Q150 22 320 17 T600 19" stroke="#5C3D1F" strokeWidth="0.5" fill="none" opacity="0.45" />
      </svg>
    </div>
  </div>
);

// ── Reading-Den window decoration (top-right corner, below the nav) ──
const Window = ({ mode }) => (
  <div
    aria-hidden="true"
    style={{
      position: "absolute",
      // Tucked below the floating nav bar so it doesn't get clipped.
      top: 140,
      right: 32,
      width: 140,
      height: 170,
      background: "#FFFFFF",
      border: "8px solid #B07F4A",
      borderRadius: 14,
      boxShadow:
        "0px 6px 0px rgba(11,26,51,0.10), 0px 18px 30px rgba(11,26,51,0.10)",
      overflow: "hidden",
      zIndex: 0,
    }}
    className="hidden lg:block"
  >
    {/* Sky inside window */}
    <div
      style={{
        position: "absolute",
        inset: 0,
        background:
          mode === "night"
            ? "linear-gradient(180deg, #1B2C5A 0%, #2A3F7B 100%)"
            : "linear-gradient(180deg, #BFE2FF 0%, #FCE4B9 100%)",
      }}
    />

    {mode === "day" ? (
      <>
        {/* Sun */}
        <div
          style={{
            position: "absolute",
            top: 18,
            right: 18,
            width: 36,
            height: 36,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, #FFE99A 0%, #FFD76A 65%, rgba(255,215,106,0) 100%)",
          }}
        />
        {/* Cloud */}
        <div
          style={{
            position: "absolute",
            top: 70,
            left: 8,
            width: 70,
            height: 26,
            background: "#FFFFFF",
            borderRadius: 30,
            opacity: 0.92,
          }}
        />
      </>
    ) : (
      <>
        {/* Moon */}
        <div
          style={{
            position: "absolute",
            top: 18,
            right: 18,
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "#F5EBC8",
            boxShadow: "0 0 18px rgba(245,235,200,0.6)",
          }}
        />
        {/* Stars */}
        {[
          { top: 22, left: 22 }, { top: 52, left: 90 }, { top: 80, left: 30 },
          { top: 102, left: 100 }, { top: 50, left: 40 }, { top: 38, left: 70 },
        ].map((s, i) => (
          <motion.span
            key={i}
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 2 + (i % 3) * 0.6, repeat: Infinity, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: s.top,
              left: s.left,
              width: 3,
              height: 3,
              borderRadius: "50%",
              background: "#FFFFFF",
              boxShadow: "0 0 4px #FFFFFF",
            }}
          />
        ))}
      </>
    )}

    {/* Window cross + sill */}
    <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 4, background: "#B07F4A", transform: "translateY(-50%)" }} />
    <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 4, background: "#B07F4A", transform: "translateX(-50%)" }} />
  </div>
);

// ── Lamp decoration (bottom-left corner) ──
const Lamp = ({ mode }) => (
  <div
    aria-hidden="true"
    style={{ position: "absolute", bottom: 6, left: 18 }}
    className="hidden md:block"
  >
    {/* Glow */}
    <div
      style={{
        position: "absolute",
        bottom: 78,
        left: -28,
        width: 160,
        height: 120,
        borderRadius: "50%",
        background:
          mode === "night"
            ? "radial-gradient(circle, rgba(255,225,150,0.45) 0%, rgba(255,225,150,0) 70%)"
            : "radial-gradient(circle, rgba(255,225,150,0.25) 0%, rgba(255,225,150,0) 70%)",
        filter: "blur(8px)",
        animation: "den-lamp-pulse 6s ease-in-out infinite",
      }}
    />
    <svg width="92" height="120" viewBox="0 0 92 120" aria-hidden="true">
      {/* Shade */}
      <path d="M16 8 L76 8 L86 50 L6 50 Z" fill="#E5874D" />
      <path d="M16 8 L76 8 L74 14 L18 14 Z" fill="#C56A33" />
      {/* Bulb glow under shade */}
      <ellipse cx="46" cy="52" rx="34" ry="6" fill={mode === "night" ? "#FFE99A" : "#FFD76A"} opacity="0.8" />
      {/* Stand */}
      <rect x="42" y="50" width="8" height="50" fill="#8E5F30" />
      <ellipse cx="46" cy="106" rx="22" ry="8" fill="#5C3D1F" />
      <ellipse cx="46" cy="104" rx="22" ry="6" fill="#8E5F30" />
    </svg>
  </div>
);

// ── Floating dust motes (day mode) ──
const DustMotes = ({ count = 14 }) => (
  <div className="absolute inset-0 pointer-events-none overflow-hidden">
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        style={{
          position: "absolute",
          top: `${(i * 53) % 100}%`,
          left: `${(i * 71) % 100}%`,
          width: 3 + (i % 3),
          height: 3 + (i % 3),
          borderRadius: "50%",
          background: "rgba(255,225,170,0.55)",
          boxShadow: "0 0 6px rgba(255,225,170,0.6)",
          animation: `den-mote ${10 + (i % 7)}s linear infinite`,
          animationDelay: `${-i * 0.6}s`,
        }}
      />
    ))}
  </div>
);

// ── Night sky: stars, shooting stars, fireflies, nebula glow ──
const NightSky = () => {
  // Deterministic but lively. Positions seeded by index.
  const stars = Array.from({ length: 60 }).map((_, i) => {
    const top = (i * 37.13) % 100;
    const left = (i * 71.79) % 100;
    const size = 1 + ((i * 13) % 4); // 1–4px
    const twinkleDur = 2 + ((i * 0.31) % 3); // 2–5s
    const delay = (i * 0.17) % 4;
    const halo = i % 7 === 0; // every 7th gets a glow halo
    return { top, left, size, twinkleDur, delay, halo, id: i };
  });

  const fireflies = Array.from({ length: 8 }).map((_, i) => ({
    top: 30 + ((i * 9.3) % 60),
    left: (i * 27.7) % 100,
    delay: (i * 1.1) % 4,
    dur: 8 + ((i * 0.7) % 6),
    id: i,
  }));

  // 4 shooting stars on different paths, staggered
  const shooting = [
    { id: 1, top: "8%",  left: "-10%", angle: 22,  delay: 0,    dur: 1.6, cycle: 9  },
    { id: 2, top: "22%", left: "-10%", angle: 18,  delay: 3.2,  dur: 1.4, cycle: 12 },
    { id: 3, top: "42%", left: "-10%", angle: 14,  delay: 6.6,  dur: 1.8, cycle: 14 },
    { id: 4, top: "60%", left: "-10%", angle: 24,  delay: 10.5, dur: 1.5, cycle: 15 },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {/* Soft nebula glow on the upper-left for depth */}
      <div
        style={{
          position: "absolute",
          top: "-12%",
          left: "-15%",
          width: 600,
          height: 600,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(120,90,200,0.25) 0%, rgba(60,80,200,0.12) 35%, rgba(0,0,0,0) 65%)",
          filter: "blur(20px)",
        }}
      />
      {/* Far-side teal nebula for contrast */}
      <div
        style={{
          position: "absolute",
          top: "30%",
          right: "-20%",
          width: 540,
          height: 540,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(0,200,255,0.16) 0%, rgba(0,100,200,0.08) 40%, rgba(0,0,0,0) 65%)",
          filter: "blur(22px)",
        }}
      />

      {/* Background stars */}
      {stars.map((s) => (
        <span
          key={`star-${s.id}`}
          style={{
            position: "absolute",
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: s.size,
            height: s.size,
            borderRadius: "50%",
            background: "#FFFFFF",
            boxShadow: s.halo
              ? "0 0 8px rgba(255,255,255,0.85), 0 0 16px rgba(0,202,255,0.4)"
              : "0 0 2px rgba(255,255,255,0.6)",
            animation: `den-twinkle ${s.twinkleDur}s ease-in-out infinite`,
            animationDelay: `${s.delay}s`,
            opacity: 0.85,
          }}
        />
      ))}

      {/* Shooting stars */}
      {shooting.map((s) => (
        <div
          key={`shoot-${s.id}`}
          style={{
            position: "absolute",
            top: s.top,
            left: s.left,
            // Outer wrapper holds the rotation so the inner translateX animates cleanly.
            transform: `rotate(${s.angle}deg)`,
            transformOrigin: "left center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              width: 140,
              height: 2,
              background:
                "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.85) 70%, #FFFFFF 100%)",
              borderRadius: 999,
              filter:
                "drop-shadow(0 0 4px rgba(255,255,255,0.85)) drop-shadow(0 0 12px rgba(0,202,255,0.6))",
              animation: `den-shoot ${s.cycle}s linear infinite`,
              animationDelay: `${s.delay}s`,
              opacity: 0,
            }}
          />
        </div>
      ))}

      {/* Fireflies (warm-tinted, drifting) */}
      {fireflies.map((f) => (
        <span
          key={`ff-${f.id}`}
          style={{
            position: "absolute",
            top: `${f.top}%`,
            left: `${f.left}%`,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: "rgba(255,225,150,0.95)",
            boxShadow:
              "0 0 8px rgba(255,225,150,0.9), 0 0 18px rgba(255,200,80,0.65)",
            animation: `den-firefly ${f.dur}s ease-in-out infinite`,
            animationDelay: `${f.delay}s`,
          }}
        />
      ))}
    </div>
  );
};

const AudiobookLibrary = () => {
  const navigate = useNavigate();
  const { apiClient } = useAPI();
  const { user } = useAuth();
  const { currentProfile, requireParentalVerification } = useProfile();
  const [audiobooks, setAudiobooks] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recommendedLoading, setRecommendedLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [mode, setMode] = useState(() => {
    const h = new Date().getHours();
    return h >= 19 || h < 6 ? "night" : "day";
  });

  // Loaders ----------------------------------------------------------------
  const loadAudiobooks = useCallback(
    async (silent = false) => {
      if (!user?.id) return;
      try {
        if (!silent) setLoading(true);
        const response = await apiClient.getUserAudiobooks(user.id, currentProfile?.id);
        if (response.status && response.data) {
          const list = Array.isArray(response.data) ? response.data : [];
          list.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
          setAudiobooks(list);
        }
      } catch (err) {
        logger.error("Error loading audiobooks:", err);
        if (!silent) setAudiobooks([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [apiClient, user?.id, currentProfile?.id]
  );

  const loadRecommended = useCallback(async () => {
    try {
      setRecommendedLoading(true);
      const resp = await apiClient.getPublicStorybooksFeed(RECOMMENDED_LIMIT, 0);
      const raw = resp?.data ?? [];
      const items = Array.isArray(raw) ? raw : [];
      const publicOnly = items.filter((i) => i.is_public === true);
      setRecommended(publicOnly);
    } catch (err) {
      logger.error("Error loading recommended storybooks:", err);
      setRecommended([]);
    } finally {
      setRecommendedLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    loadAudiobooks();
    loadRecommended();
    const handleDelete = () => loadAudiobooks();
    window.addEventListener("audiobook:deleted", handleDelete);
    return () => window.removeEventListener("audiobook:deleted", handleDelete);
  }, [loadAudiobooks, loadRecommended]);

  // Poll for in-progress audiobooks
  useEffect(() => {
    const hasProcessing = audiobooks.some(
      (a) => a.status?.toLowerCase() === "processing" || a.status?.toLowerCase() === "creating"
    );
    if (!hasProcessing) return;
    const id = setInterval(() => loadAudiobooks(true), 3000);
    return () => clearInterval(id);
  }, [audiobooks, loadAudiobooks]);

  // Derived data -----------------------------------------------------------
  const myBooks = useMemo(() => audiobooks.map(audiobookToStory), [audiobooks]);
  const recommendedBooks = useMemo(
    () => recommended.map((b) => ({
      id: b.id,
      title: b.title,
      name: b.title,
      thumbnail_url: b.cover_url || b.cover_image_url || b.thumbnail_url || null,
      cover_image_url: b.cover_image_url || b.cover_url || b.thumbnail_url || null,
      file_url: "ready",
      status: "ready",
    })),
    [recommended]
  );

  // Continue Reading: pick the most recently-touched in-progress storybook.
  // A progressive book (readable while still generating) shows as "Ready to
  // read" with pages-ready — its `progress` is GENERATION progress, which
  // must never masquerade as reading progress.
  const continueReading = useMemo(() => {
    const inProgress = audiobooks.filter((a) => {
      const p = a.progress || a.percentage || 0;
      const readable = a.status === "ready" || a.status === "completed";
      const stillGenerating = a.generation_complete === false;
      return readable && (stillGenerating || (p > 0 && p < 100));
    });
    if (!inProgress.length) return null;
    return inProgress[0];
  }, [audiobooks]);
  const continueIsGenerating = continueReading?.generation_complete === false;

  const myBooksRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < myBooks.length; i += STORYBOOKS_PER_ROW)
      rows.push(myBooks.slice(i, i + STORYBOOKS_PER_ROW));
    return rows;
  }, [myBooks]);
  const recommendedRows = useMemo(() => {
    const rows = [];
    for (let i = 0; i < recommendedBooks.length; i += STORYBOOKS_PER_ROW)
      rows.push(recommendedBooks.slice(i, i + STORYBOOKS_PER_ROW));
    return rows;
  }, [recommendedBooks]);

  const visibleMyRows = showAll ? myBooksRows : myBooksRows.slice(0, DEFAULT_ROWS);
  const hasMoreRows = myBooksRows.length > DEFAULT_ROWS;

  // Handlers ---------------------------------------------------------------
  const openStorybook = (book) => navigate(`/audiobooks/${book.id}`);

  const handleDelete = (id) => {
    requireParentalVerification("delete this storybook", async () => {
      try {
        const resp = await apiClient.deleteAudiobook(id);
        if (resp.status) {
          toast.success("Storybook deleted");
          loadAudiobooks();
        } else {
          toast.error(resp.error || "Failed to delete storybook");
        }
      } catch (err) {
        logger.error("Delete failed:", err);
        toast.error("Failed to delete storybook");
      }
    });
  };
  // expose for future use (e.g. long-press); not wired into PictureBook yet
  void handleDelete;

  if (loading) return <LoadingScreen message="Loading storybooks..." />;

  // Reading Den palette: day = brand cream-blue, night = deep evening blue
  const pageBg = mode === "day"
    ? "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)"
    : "linear-gradient(180deg, #1B2C5A 0%, #243661 55%, #2C3F6A 100%)";
  const headingColor = mode === "day" ? "#0B1A33" : "#FFFFFF";
  const subTextColor = mode === "day" ? "#5A6B85" : "rgba(255,255,255,0.75)";
  const labelColor = mode === "day" ? "#0065F8" : "#FFD76A";

  return (
    <div
      className="min-h-screen pt-24 sm:pt-32 pb-16 relative"
      style={{
        background: pageBg,
        fontFamily: "'Inter', 'Fredoka', sans-serif",
        paddingBottom: 1,
        transition: "background 700ms ease",
      }}
    >
      <Window mode={mode} />
      {mode === "day" ? <DustMotes count={18} /> : <NightSky />}

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <LibraryContentTabs active="storybooks" className="mb-6" />

        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <span
              className="inline-block mb-1"
              style={{
                ...FREDOKA,
                color: labelColor,
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              The Reading Den
            </span>
            <h1
              style={{
                ...FREDOKA,
                color: headingColor,
                fontWeight: 700,
                fontSize: "clamp(2rem, 4.6vw, 3rem)",
                letterSpacing: "-0.02em",
                textTransform: "uppercase",
                lineHeight: 1.05,
                margin: 0,
              }}
            >
              Storybooks
            </h1>
            <p style={{ ...FREDOKA, color: subTextColor, fontSize: 15, margin: "6px 0 0" }}>
              Pick a book — your friends are waiting on the shelves.
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Day / Night toggle */}
            <button
              type="button"
              onClick={() => setMode((m) => (m === "day" ? "night" : "day"))}
              aria-label={`Switch to ${mode === "day" ? "night" : "day"} mode`}
              className="inline-flex items-center justify-center transition active:scale-[0.96]"
              style={{
                ...FREDOKA,
                width: 44,
                height: 44,
                background: mode === "day" ? "#FFFFFF" : "rgba(255,255,255,0.10)",
                color: mode === "day" ? "#0065F8" : "#FFD76A",
                border: mode === "day" ? "2px solid rgba(11,26,51,0.08)" : "2px solid rgba(255,255,255,0.20)",
                borderRadius: 14,
                boxShadow: mode === "day" ? "0px 2px 0px rgba(11,26,51,0.06)" : "0px 2px 0px rgba(0,0,0,0.30)",
                cursor: "pointer",
              }}
            >
              {mode === "day" ? (
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="4" />
                  <path strokeLinecap="round" d="M12 2v2M12 20v2M2 12h2M20 12h2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                </svg>
              ) : (
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                </svg>
              )}
            </button>

            {/* AI Book Creation (hidden) */}
            {false && (
            <button
              type="button"
              onClick={() => navigate("/audiobooks/create-ai-voice")}
              className="inline-flex items-center gap-2 px-5 py-2.5 transition active:scale-[0.98]"
              style={{
                ...FREDOKA,
                background: "#FFFFFF",
                color: "#0065F8",
                fontWeight: 700,
                fontSize: 14,
                border: "2px solid rgba(0,101,248,0.22)",
                borderRadius: 999,
                boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
                cursor: "pointer",
              }}
            >
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l1.6 5.2L19 9l-5.4 1.8L12 16l-1.6-5.2L5 9l5.4-1.8L12 2zM19 14l.9 2.9L23 18l-3.1.9L19 22l-.9-3.1L15 18l3.1-1.1L19 14z" />
              </svg>
              AI Book Creation
            </button>
            )}

            {/* Create */}
            <button
              type="button"
              onClick={() => navigate("/audiobooks/create")}
              className="inline-flex items-center gap-2 px-5 py-2.5 transition active:scale-[0.98]"
              style={{
                ...FREDOKA,
                background: "#0065F8",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 14,
                border: "none",
                borderRadius: 999,
                boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                cursor: "pointer",
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              New storybook
            </button>
          </div>
        </div>

        {/* Continue reading callout */}
        {continueReading && (
          <motion.button
            type="button"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -2 }}
            onClick={() => openStorybook({ id: continueReading.id })}
            className="w-full text-left mb-8 flex items-center gap-4 p-4 transition"
            style={{
              background: mode === "day" ? "#FFFFFF" : "rgba(255,255,255,0.06)",
              border: mode === "day" ? "2px solid rgba(11,26,51,0.06)" : "2px solid rgba(255,255,255,0.10)",
              borderRadius: 22,
              boxShadow: mode === "day"
                ? "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 24px rgba(11,26,51,0.08)"
                : "0px 4px 0px rgba(0,0,0,0.30)",
              cursor: "pointer",
            }}
          >
            <img
              src={continueReading.cover_url || continueReading.cover_image_url || continueReading.thumbnail_url || "/assets/story-placeholder.png"}
              alt=""
              className="w-16 h-20 sm:w-20 sm:h-24 object-cover flex-shrink-0"
              style={{ borderRadius: 10, boxShadow: "0px 4px 8px rgba(11,26,51,0.18)" }}
            />
            <div className="flex-1 min-w-0">
              <span
                style={{
                  ...FREDOKA,
                  color: labelColor,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                }}
              >
                {continueIsGenerating ? "Ready to read — still generating" : "Continue reading"}
              </span>
              <h3
                style={{
                  ...FREDOKA,
                  color: headingColor,
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: "-0.01em",
                  margin: "2px 0 4px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {continueReading.title || "Your storybook"}
              </h3>
              <div className="flex items-center gap-2">
                <div
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 999,
                    background: mode === "day" ? "rgba(11,26,51,0.08)" : "rgba(255,255,255,0.15)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${
                        continueIsGenerating
                          ? Math.min(
                              100,
                              (100 * (continueReading.pages_ready_count || 0)) /
                                Math.max(1, continueReading.total_pages || 1)
                            )
                          : continueReading.progress || continueReading.percentage || 0
                      }%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
                    }}
                  />
                </div>
                <span style={{ ...FREDOKA, color: subTextColor, fontSize: 12, fontWeight: 700 }}>
                  {continueIsGenerating
                    ? `${continueReading.pages_ready_count || 0}/${continueReading.total_pages || "?"} pages`
                    : `${Math.round(continueReading.progress || continueReading.percentage || 0)}%`}
                </span>
              </div>
            </div>
            <span
              className="hidden sm:inline-flex items-center gap-1 px-4 py-2"
              style={{
                ...FREDOKA,
                background: "#0065F8",
                color: "#FFFFFF",
                fontWeight: 700,
                fontSize: 13,
                border: "none",
                borderRadius: 999,
                boxShadow: "0px 3px 0px #073B8E",
              }}
            >
              {continueIsGenerating ? "Start reading" : "Pick up"}
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </span>
          </motion.button>
        )}

        {/* MY STORYBOOKS */}
        <section className="mb-12">
          <div className="flex items-end justify-between mb-3">
            <div>
              <span
                className="inline-block mb-0.5"
                style={{
                  ...FREDOKA,
                  color: labelColor,
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                }}
              >
                Your library
              </span>
              <h2
                style={{
                  ...FREDOKA,
                  color: headingColor,
                  fontWeight: 700,
                  fontSize: 22,
                  letterSpacing: "-0.01em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                My Storybooks
              </h2>
            </div>
            {hasMoreRows && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="inline-flex items-center gap-1 transition"
                style={{
                  ...FREDOKA,
                  color: mode === "day" ? "#0065F8" : "#FFD76A",
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                {showAll ? "Show less" : `View all (${audiobooks.length})`}
                <svg
                  width="14"
                  height="14"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  viewBox="0 0 24 24"
                  style={{ transition: "transform 200ms", transform: showAll ? "rotate(-90deg)" : "rotate(90deg)" }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
                </svg>
              </button>
            )}
          </div>

          {audiobooks.length === 0 ? (
            <EmptyState
              mode={mode}
              title="No storybooks yet"
              subtitle="Tap “New storybook” to add the first book to your shelves."
              ctaLabel="+ Create your first storybook"
              onCta={() => navigate("/audiobooks/create")}
            />
          ) : (
            <div className="space-y-4">
              {visibleMyRows.map((row, rIdx) => (
                <ShelfRow
                  key={`my-row-${rIdx}`}
                  books={row}
                  offsetIndex={rIdx * STORYBOOKS_PER_ROW}
                  onBookClick={openStorybook}
                />
              ))}
            </div>
          )}
        </section>

        {/* PICKED FOR YOU */}
        <section>
          <div className="mb-3">
            <span
              className="inline-block mb-0.5"
              style={{
                ...FREDOKA,
                color: labelColor,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
              }}
            >
              From the wider library
            </span>
            <h2
              style={{
                ...FREDOKA,
                color: headingColor,
                fontWeight: 700,
                fontSize: 22,
                letterSpacing: "-0.01em",
                textTransform: "uppercase",
                margin: 0,
              }}
            >
              Picked for you
            </h2>
          </div>

          {recommendedLoading ? (
            <div className="space-y-4">
              {[0, 1].map((r) => (
                <div key={r}>
                  <div
                    className="grid gap-3 sm:gap-4 px-3 sm:px-4 pb-1"
                    style={{
                      gridTemplateColumns: `repeat(${STORYBOOKS_PER_ROW}, minmax(0, 1fr))`,
                    }}
                  >
                    {Array.from({ length: STORYBOOKS_PER_ROW }).map((_, i) => (
                      <div
                        key={i}
                        className="animate-pulse"
                        style={{
                          aspectRatio: "3/4",
                          background: mode === "day" ? "rgba(11,26,51,0.06)" : "rgba(255,255,255,0.08)",
                          borderRadius: 12,
                        }}
                      />
                    ))}
                  </div>
                  <div
                    style={{
                      height: 26,
                      marginTop: -2,
                      borderRadius: 10,
                      background: "linear-gradient(180deg, #C99564 0%, #B07F4A 60%, #8E5F30 100%)",
                      opacity: 0.55,
                    }}
                  />
                </div>
              ))}
            </div>
          ) : recommendedRows.length === 0 ? (
            <EmptyState
              mode={mode}
              title="The library is quiet"
              subtitle="No public storybooks to recommend right now — check back soon!"
            />
          ) : (
            <div className="space-y-4">
              {recommendedRows.map((row, rIdx) => (
                <ShelfRow
                  key={`rec-row-${rIdx}`}
                  books={row}
                  offsetIndex={rIdx * STORYBOOKS_PER_ROW + 1000}
                  onBookClick={openStorybook}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Lamp at the very bottom-left of the page */}
      <Lamp mode={mode} />

      <style>{`
        @keyframes den-lamp-pulse {
          0%, 100% { opacity: 0.8; transform: scale(1); }
          50%      { opacity: 1;   transform: scale(1.04); }
        }
        @keyframes den-mote {
          0%   { transform: translate(0, 0); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translate(20vw, -50vh); opacity: 0; }
        }
        @keyframes den-twinkle {
          0%, 100% { opacity: 0.25; transform: scale(0.9); }
          50%      { opacity: 1;    transform: scale(1.1); }
        }
        @keyframes den-shoot {
          0%   { transform: translateX(0) scaleX(0);    opacity: 0; }
          3%   { transform: translateX(0) scaleX(0.6);  opacity: 0.85; }
          14%  { transform: translateX(120vw) scaleX(1); opacity: 0.95; }
          18%  { transform: translateX(140vw) scaleX(0.6); opacity: 0; }
          100% { transform: translateX(140vw) scaleX(0);   opacity: 0; }
        }
        @keyframes den-firefly {
          0%, 100% { transform: translate(0, 0) scale(0.85); opacity: 0.45; }
          25%      { transform: translate(20px, -16px) scale(1.15); opacity: 1; }
          50%      { transform: translate(-12px, 22px) scale(0.95); opacity: 0.85; }
          75%      { transform: translate(16px, 8px)   scale(1.05); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

// ── Empty state card (used twice) ──
const EmptyState = ({ mode, title, subtitle, ctaLabel, onCta }) => (
  <div
    className="p-8 text-center"
    style={{
      background: mode === "day" ? "#FFFFFF" : "rgba(255,255,255,0.06)",
      border: mode === "day" ? "2px solid rgba(11,26,51,0.06)" : "2px solid rgba(255,255,255,0.10)",
      borderRadius: 22,
      boxShadow:
        mode === "day"
          ? "0px 2px 0px rgba(11,26,51,0.04), 0px 8px 20px rgba(11,26,51,0.06)"
          : "0px 4px 0px rgba(0,0,0,0.30)",
    }}
  >
    <p style={{ ...FREDOKA, color: mode === "day" ? "#0B1A33" : "#FFFFFF", fontWeight: 700, fontSize: 18, margin: "0 0 6px" }}>
      {title}
    </p>
    <p style={{ ...FREDOKA, color: mode === "day" ? "#5A6B85" : "rgba(255,255,255,0.75)", fontSize: 14, margin: ctaLabel ? "0 0 16px" : 0 }}>
      {subtitle}
    </p>
    {ctaLabel && (
      <button
        type="button"
        onClick={onCta}
        className="px-5 py-2.5 transition active:scale-[0.98]"
        style={{
          ...FREDOKA,
          background: "#0065F8",
          color: "#FFFFFF",
          fontWeight: 700,
          fontSize: 14,
          border: "none",
          borderRadius: 999,
          boxShadow: "0px 4px 0px #073B8E",
          cursor: "pointer",
        }}
      >
        {ctaLabel}
      </button>
    )}
  </div>
);

export default AudiobookLibrary;
